import React, { useState, useEffect } from "react"

const ManualAssignmentForm = ({ assignment, courses, onSubmit, onCancel, isLoading }) => {
  const [name, setName] = useState(assignment?.name || "")
  const [description, setDescription] = useState(assignment?.description || "")
  const [courseId, setCourseId] = useState(assignment?.course?.id || "")
  const [dueAt, setDueAt] = useState("")
  const [pointsPossible, setPointsPossible] = useState(assignment?.points_possible || "")
  const [htmlUrl, setHtmlUrl] = useState(assignment?.html_url || "")
  const [errors, setErrors] = useState({})

  useEffect(() => {
    if (assignment) {
      setName(assignment.name || "")
      setDescription(assignment.description || "")
      setCourseId(assignment.course?.id || "")
      setPointsPossible(assignment.points_possible || "")
      setHtmlUrl(assignment.html_url || "")

      // Format due_at for datetime-local input
      if (assignment.due_at) {
        const date = new Date(assignment.due_at)
        const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
        setDueAt(localDate.toISOString().slice(0, 16))
      } else {
        setDueAt("")
      }
    }
  }, [assignment])

  // Set default course if only one is available
  useEffect(() => {
    if (!assignment && courses?.length === 1 && !courseId) {
      setCourseId(courses[0].id)
    }
  }, [courses, assignment, courseId])

  const validate = () => {
    const newErrors = {}
    if (!name.trim()) {
      newErrors.name = "Assignment name is required"
    }
    if (!courseId) {
      newErrors.courseId = "Please select a course"
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (validate()) {
      onSubmit({
        name: name.trim(),
        description: description.trim() || null,
        course_id: parseInt(courseId, 10),
        due_at: dueAt ? new Date(dueAt).toISOString() : null,
        points_possible: pointsPossible ? parseFloat(pointsPossible) : null,
        html_url: htmlUrl.trim() || null,
      })
    }
  }

  const inputBaseClass = `
    w-full px-3 py-2.5 text-sm border rounded-lg
    bg-gray-50/50
    focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 focus:bg-white
    transition-all duration-200
    placeholder:text-gray-400
  `

  return (
    <form onSubmit={handleSubmit} className="flex flex-col">
      {/* Scrollable content area */}
      <div className="flex-1 overflow-y-auto px-1 -mx-1 space-y-4">
        {/* Course selector - only show when creating new assignment */}
        {!assignment && (
          <div>
            <label htmlFor="assignment-course" className="block text-sm font-medium text-gray-700 mb-1.5">
              Course <span className="text-red-500">*</span>
            </label>
            <select
              id="assignment-course"
              value={courseId}
              onChange={(e) => setCourseId(e.target.value)}
              className={`${inputBaseClass} ${errors.courseId ? "border-red-300 bg-red-50/30" : "border-gray-200"}`}
              disabled={isLoading}
            >
              <option value="">Select a course</option>
              {courses?.map((course) => (
                <option key={course.id} value={course.id}>
                  {course.course_code ? `${course.course_code} - ${course.name}` : course.name}
                </option>
              ))}
            </select>
            {errors.courseId && (
              <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {errors.courseId}
              </p>
            )}
          </div>
        )}

        {/* Assignment Name */}
        <div>
          <label htmlFor="assignment-name" className="block text-sm font-medium text-gray-700 mb-1.5">
            Assignment Name <span className="text-red-500">*</span>
          </label>
          <input
            id="assignment-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Chapter 3 Reading"
            className={`${inputBaseClass} ${errors.name ? "border-red-300 bg-red-50/30" : "border-gray-200"}`}
            disabled={isLoading}
          />
          {errors.name && (
            <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {errors.name}
            </p>
          )}
        </div>

        {/* Due Date and Points in a row */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="assignment-due" className="block text-sm font-medium text-gray-700 mb-1.5">
              Due Date
            </label>
            <input
              id="assignment-due"
              type="datetime-local"
              value={dueAt}
              onChange={(e) => setDueAt(e.target.value)}
              className={`${inputBaseClass} border-gray-200`}
              disabled={isLoading}
            />
          </div>

          <div>
            <label htmlFor="assignment-points" className="block text-sm font-medium text-gray-700 mb-1.5">
              Points
            </label>
            <input
              id="assignment-points"
              type="number"
              min="0"
              step="0.5"
              value={pointsPossible}
              onChange={(e) => setPointsPossible(e.target.value)}
              placeholder="e.g., 100"
              className={`${inputBaseClass} border-gray-200`}
              disabled={isLoading}
            />
          </div>
        </div>

        {/* Link to Assignment Details */}
        <div>
          <label htmlFor="assignment-url" className="block text-sm font-medium text-gray-700 mb-1.5">
            Link to Assignment
            <span className="ml-1.5 text-xs font-normal text-gray-400">(optional)</span>
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
            </div>
            <input
              id="assignment-url"
              type="url"
              value={htmlUrl}
              onChange={(e) => setHtmlUrl(e.target.value)}
              placeholder="https://..."
              className={`${inputBaseClass} border-gray-200 pl-9`}
              disabled={isLoading}
            />
          </div>
        </div>

        {/* Description */}
        <div>
          <label htmlFor="assignment-description" className="block text-sm font-medium text-gray-700 mb-1.5">
            Notes
            <span className="ml-1.5 text-xs font-normal text-gray-400">(optional)</span>
          </label>
          <textarea
            id="assignment-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Add any notes about this assignment..."
            rows={2}
            className={`${inputBaseClass} border-gray-200 resize-none`}
            disabled={isLoading}
          />
        </div>
      </div>

      {/* Footer with buttons */}
      <div className="flex gap-3 pt-5 mt-4 border-t border-gray-100">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 hover:text-gray-700 transition-all duration-200"
          disabled={isLoading}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow"
          disabled={isLoading}
        >
          {isLoading ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Saving...
            </span>
          ) : assignment ? "Update" : "Add Assignment"}
        </button>
      </div>
    </form>
  )
}

export default ManualAssignmentForm
